import { isbot } from 'isbot';
import type { ExperimentApi, ExperimentStatePort, ExperimentTelemetry } from './ports.js';
import type { ExperimentEntry, ExperimentRequestContext, GetExperimentsPayload } from './types.js';

/** Variant assigned to bot sessions when no experiments exist yet. */
const BOT_DEFAULT_VARIANT = 'control_variant';

/** UA suffix used by classic/Playwright/WDIO to mark automated test sessions. */
const AUTOMATED_TESTING_UA_MARKER = 'isAutomatedTesting:true';

/**
 * Construction dependencies for {@link ExperimentClient}.
 * `TEventMetadata` is the client-defined `event_metadata` shape.
 */
export interface ExperimentClientConfig<TEventMetadata = unknown> {
	api: ExperimentApi<TEventMetadata>;
	state: ExperimentStatePort;
	telemetry?: ExperimentTelemetry;
}

/**
 * Framework-agnostic experiment client: a single Get at a divergence point with
 * auto_mark_seen, bot handling, and synchronous variant lookups against injected
 * state. The host supplies the per-call request context; this client only builds
 * the payload envelope and merges results back into state.
 * `TEventMetadata` is the client-defined `event_metadata` shape.
 */
export class ExperimentClient<TEventMetadata = unknown> {
	readonly #api: ExperimentApi<TEventMetadata>;
	readonly #state: ExperimentStatePort;
	readonly #telemetry?: ExperimentTelemetry;

	constructor({ api, state, telemetry }: ExperimentClientConfig<TEventMetadata>) {
		this.#api = api;
		this.#state = state;
		this.#telemetry = telemetry;
	}

	/**
	 * Returns the assigned variant for an experiment at a divergence point and records exposure (Get with auto_mark_seen).
	 * @param experimentKey - Experiment key (e.g. from ACTIVE_EXPERIMENTS).
	 * @param context - Per-call request context (host, profile, user, data, eventMetadata) built by the host.
	 * @param autoMarkSeen - Whether to mark the experiment as seen.
	 * @returns The assigned variant string, or null if not assigned or the API failed.
	 */
	async initializeExperiment(
		experimentKey: string,
		context: ExperimentRequestContext<TEventMetadata>,
		autoMarkSeen = true
	): Promise<string | null> {
		if (!experimentKey) return null;

		const isBotSession = await this.#handleBotExperimentAssignment(experimentKey);
		if (isBotSession) return null;

		const payload: GetExperimentsPayload<TEventMetadata> = {
			auto_mark_seen: autoMarkSeen,
			data: context.data,
			event_metadata: context.eventMetadata,
			experiment_keys: [experimentKey],
			profile: context.profile,
			user: context.user,
		};
		const results = await this.#getExperiments(context.host, payload);
		const experiment = results.find((exp) => exp.key === experimentKey);
		return experiment?.variant ?? null;
	}

	/**
	 * Fetches multiple experiment assignments in one Get request.
	 * @param experimentKeys - Experiment keys to request together.
	 * @param context - Per-call request context (host, profile, user, data, eventMetadata) built by the host.
	 * @param autoMarkSeen - Whether to mark every returned experiment as seen.
	 * @returns The merged experiment state after the Get, or existing state on failure.
	 */
	async initializeExperiments(
		experimentKeys: string[],
		context: ExperimentRequestContext<TEventMetadata>,
		autoMarkSeen = false
	): Promise<ExperimentEntry[]> {
		const requestedKeys = [...new Set(experimentKeys.filter(Boolean))];
		if (requestedKeys.length === 0) return [];

		const isBotSession = await this.#handleBotExperimentAssignment(requestedKeys);
		if (isBotSession) return [];

		const payload: GetExperimentsPayload<TEventMetadata> = {
			auto_mark_seen: autoMarkSeen,
			data: context.data,
			event_metadata: context.eventMetadata,
			experiment_keys: requestedKeys,
			profile: context.profile,
			user: context.user,
		};
		return this.#getExperiments(context.host, payload);
	}

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variant - Variant string to compare (e.g. 'test', 'default').
	 * @returns true if the stored variant matches, false otherwise or if missing.
	 */
	isVariantActive(experimentKey: string, variant: string): boolean {
		const experiments = this.#state.getExperiments();
		if (!experiments?.length || !experimentKey) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant === variant;
	}

	/**
	 * @param experimentKey - Experiment key to look up.
	 * @param variants - List of variant strings (e.g. ['test', 'test-a']).
	 * @returns true if the stored variant is in the list, false otherwise or if missing.
	 */
	isAnyVariantActive(experimentKey: string, variants: string[]): boolean {
		const experiments = this.#state.getExperiments();
		if (!experiments?.length || !experimentKey || !Array.isArray(variants)) return false;
		const experiment = experiments.find((exp) => exp.key === experimentKey);
		return experiment?.variant != null && variants.includes(experiment.variant);
	}

	/**
	 * @param experimentKeys - List of experiment keys to check.
	 * @returns true if any key is assigned, false if none or state empty.
	 */
	isAnyAssigned(experimentKeys: string[]): boolean {
		const experiments = this.#state.getExperiments();
		if (!Array.isArray(experimentKeys) || !experimentKeys.length || !experiments?.length) {
			return false;
		}
		for (const key of experimentKeys) {
			const experiment = experiments.find((exp) => exp.key === key);
			if (experiment?.assigned || typeof experiment?.experiment_id === 'string') return true;
		}
		return false;
	}

	/**
	 * Handles bot sessions: writes a default control experiment to state if empty, or reports a debug message; returns true so the caller skips the API.
	 * @param experimentKeys - Experiment key(s) to create default assignments for when state is empty.
	 * @returns true if this was a bot session (caller should skip Get), false otherwise.
	 */
	async #handleBotExperimentAssignment(experimentKeys: string | string[]): Promise<boolean> {
		const userAgent = typeof navigator !== 'undefined' ? (navigator?.userAgent ?? '') : '';
		// WDIO/Playwright sessions append this marker; they are not analytics bots.
		if (userAgent.includes(AUTOMATED_TESTING_UA_MARKER)) return false;
		if (!isbot(userAgent)) return false;

		const keys = Array.isArray(experimentKeys) ? experimentKeys : [experimentKeys];
		const existingExperiments = this.#state.getExperiments();
		if (!existingExperiments || existingExperiments.length === 0) {
			const defaultExperiments: ExperimentEntry[] = keys.map((experimentKey) => ({
				assigned: false,
				experiment_id: null,
				key: experimentKey,
				seen: false,
				variant: BOT_DEFAULT_VARIANT,
			}));
			await this.#state.setExperiments(defaultExperiments);
		} else {
			this.#telemetry?.captureMessage('ExperimentClient: bot session detected', {
				level: 'warning',
				extra: { user_agent: userAgent },
			});
		}
		return true;
	}

	/**
	 * Calls the experiment Get API, merges results into state, and returns the merged list (or existing on error).
	 * @param host - Experiment API host for the current environment.
	 * @param payload - Get payload (user, experiment_keys, profile, data, auto_mark_seen, optional event_metadata).
	 * @returns Merged experiment array on success, or existing experiments on failure.
	 */
	async #getExperiments(
		host: string,
		payload: GetExperimentsPayload<TEventMetadata>
	): Promise<ExperimentEntry[]> {
		let existingExperiments: ExperimentEntry[] = [];
		try {
			existingExperiments = this.#state.getExperiments() ?? [];

			const response = await this.#api.getExperiments(host, payload);
			const newExperiments = response?.results ?? [];

			const autoMarkSeen = payload.auto_mark_seen === true;
			const experimentsForStore: ExperimentEntry[] = newExperiments.map((exp) => ({
				assigned: exp.assigned ?? false,
				experiment_id: exp.experiment_id ?? null,
				key: exp.key,
				seen: autoMarkSeen,
				variant: exp.variant,
			}));

			const requestedKeys = new Set(payload.experiment_keys ?? []);
			const returnedKeys = new Set(newExperiments.map((exp) => exp.key));
			// Re-read state AFTER the network await (not the pre-Get snapshot) so
			// concurrent initializeExperiment calls don't clobber each other's
			// writes. The read-merge-write below has no `await` between the read
			// and `setExperiments`, so it is atomic for a synchronous state port.
			const currentExperiments = this.#state.getExperiments() ?? [];
			const experimentsMap = new Map<string, ExperimentEntry>();
			for (const exp of currentExperiments) experimentsMap.set(exp.key, { ...exp });
			for (const exp of experimentsForStore) experimentsMap.set(exp.key, exp);
			for (const key of requestedKeys) {
				if (!returnedKeys.has(key)) experimentsMap.delete(key);
			}
			const merged = [...experimentsMap.values()];
			await this.#state.setExperiments(merged);
			return merged;
		} catch (error) {
			this.#telemetry?.captureException(error, {
				extra: { action: 'Error: Failed To Get Experiments' },
			});
			return existingExperiments;
		}
	}
}
